// src/components/DeploymentModeManager.js
// Super Admin component — assign deployment mode to vendors
// Drop inside SuperAdminPanel.js as a new tab: '🏗 Deployment'

import React, { useState, useEffect } from 'react';
import { DEPLOYMENT_MODES, DEPLOYMENT_MODE_LABELS, DEPLOYMENT_MODE_DESCRIPTIONS, TAB_VISIBILITY } from '../constants/deploymentModes';
import api from '../services/api';

const MODE_ICONS = {
  STANDALONE_FULL:        '🏪',
  STANDALONE_POS_BRIDGE:  '🔌',
  MALL_MANAGED:           '🏬',
  MALL_PLUS_STALL:        '🏬+🏪',
  TABLE_QR_ONLY:          '📱',
};

const MODE_COLOURS = {
  STANDALONE_FULL:        'badge-blue',
  STANDALONE_POS_BRIDGE:  'badge-green',
  MALL_MANAGED:           'badge-amber',
  MALL_PLUS_STALL:        'badge-purple',
  TABLE_QR_ONLY:          'badge-teal',
};

export default function DeploymentModeManager() {
  const [vendors,  setVendors]  = useState([]);
  const [malls,    setMalls]    = useState([]);
  const [editing,  setEditing]  = useState(null);
  const [msg,      setMsg]      = useState('');
  const [loading,  setLoading]  = useState(false);
  const [filter,   setFilter]   = useState('ALL');

  const showMsg = (t) => { setMsg(t); setTimeout(() => setMsg(''), 3000); };

  useEffect(() => {
    api.get('/vendors').then(r => setVendors(r.data || [])).catch(() => {});
    api.get('/super-admin/malls').then(r => setMalls(r.data || [])).catch(() => {});
  }, []);

  const handleSave = async (vendorId, deploymentMode, mallId) => {
    setLoading(true);
    try {
      const soloModes = ['STANDALONE_FULL','STANDALONE_POS_BRIDGE','TABLE_QR_ONLY'];
      const effectiveMallId = soloModes.includes(deploymentMode) ? null : (mallId || null);
      await api.patch(`/vendors/${vendorId}`, { deploymentMode, mallId: effectiveMallId });
      setVendors(prev => prev.map(v => v.id === vendorId ? { ...v, deploymentMode, mallId: effectiveMallId } : v));
      setEditing(null);
      showMsg('✅ Deployment mode updated');
    } catch (err) {
      showMsg('❌ ' + (err.response?.data?.error || 'Update failed'));
    } finally { setLoading(false); }
  };

  const filteredVendors = filter === 'ALL'
    ? vendors
    : vendors.filter(v => v.deploymentMode === filter);

  // Count by mode
  const counts = Object.keys(DEPLOYMENT_MODES).reduce((acc, m) => {
    acc[m] = vendors.filter(v => v.deploymentMode === m).length;
    return acc;
  }, {});

  return (
    <div>
      {msg && (
        <div style={{ padding:'10px 16px', borderRadius:8, marginBottom:16, fontSize:13,
          background:msg.startsWith('✅')?'#1a3d2a':'#3d1a1a',
          color:msg.startsWith('✅')?'var(--green)':'var(--red)',
          border:`1px solid ${msg.startsWith('✅')?'var(--green)':'var(--red)'}`}}>
          {msg}
        </div>
      )}

      {/* Mode summary cards */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(5,1fr)', gap:10, marginBottom:24 }}>
        {Object.keys(DEPLOYMENT_MODES).map(mode => (
          <div key={mode}
            onClick={() => setFilter(filter === mode ? 'ALL' : mode)}
            style={{ background: filter===mode ? 'var(--bg3)' : 'var(--bg2)', border:`1px solid ${filter===mode?'var(--accent)':'var(--border)'}`, borderRadius:10, padding:'12px 10px', cursor:'pointer', textAlign:'center', transition:'all .15s' }}>
            <div style={{ fontSize:20, marginBottom:6 }}>{MODE_ICONS[mode]}</div>
            <div style={{ fontSize:20, fontWeight:700, color:'var(--accent)' }}>{counts[mode] || 0}</div>
            <div style={{ fontSize:10, color:'var(--text3)', marginTop:2, lineHeight:1.4 }}>
              {DEPLOYMENT_MODE_LABELS[mode].split('—')[1]?.trim() || mode}
            </div>
          </div>
        ))}
      </div>

      {/* Vendor list */}
      <div className="card" style={{ padding:0, overflow:'hidden' }}>
        <div style={{ padding:'13px 16px', borderBottom:'1px solid var(--border)', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
          <span style={{ fontWeight:700 }}>
            Vendors ({filteredVendors.length}{filter !== 'ALL' ? ` · ${DEPLOYMENT_MODE_LABELS[filter]}` : ''})
          </span>
          {filter !== 'ALL' && (
            <button className="btn btn-ghost btn-sm" onClick={() => setFilter('ALL')}>Show all</button>
          )}
        </div>

        {filteredVendors.length === 0 && (
          <div style={{ textAlign:'center', padding:40, color:'var(--text3)' }}>No vendors found.</div>
        )}

        {filteredVendors.map(vendor => (
          <div key={vendor.id}>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'12px 16px', borderBottom:'1px solid var(--border)' }}>
              <div style={{ display:'flex', alignItems:'center', gap:12 }}>
                <span style={{ fontSize:22 }}>{vendor.emoji || '🍽'}</span>
                <div>
                  <div style={{ fontWeight:600, display:'flex', alignItems:'center', gap:8 }}>
                    {vendor.name}
                    {!vendor.isActive && <span className="badge badge-red" style={{ fontSize:10 }}>Inactive</span>}
                  </div>
                  <div style={{ fontSize:12, color:'var(--text2)' }}>{vendor.email}</div>
                </div>
              </div>
              <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                <span className={`badge ${MODE_COLOURS[vendor.deploymentMode] || 'badge-gray'}`} style={{ fontSize:11 }}>
                  {MODE_ICONS[vendor.deploymentMode]} {DEPLOYMENT_MODE_LABELS[vendor.deploymentMode] || vendor.deploymentMode}
                </span>
                {vendor.mallId && (
                  <span className="badge badge-amber" style={{ fontSize:10 }}>
                    🏬 {malls.find(m => m.id === vendor.mallId)?.name || 'Mall'}
                  </span>
                )}
                <button className="btn btn-ghost btn-sm"
                  onClick={() => setEditing(editing?.id === vendor.id ? null : { ...vendor })}>
                  {editing?.id === vendor.id ? 'Cancel' : 'Edit'}
                </button>
              </div>
            </div>

            {/* Inline edit form */}
            {editing?.id === vendor.id && (
              <DeploymentEditForm
                vendor={editing}
                malls={malls}
                onSave={(mode, mallId) => handleSave(vendor.id, mode, mallId)}
                onCancel={() => setEditing(null)}
                loading={loading}
              />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function DeploymentEditForm({ vendor, malls, onSave, onCancel, loading }) {
  const [mode,   setMode]   = useState(vendor.deploymentMode || DEPLOYMENT_MODES.STANDALONE_FULL);
  const [mallId, setMallId] = useState(vendor.mallId || '');

  const needsMall = [DEPLOYMENT_MODES.MALL_MANAGED, DEPLOYMENT_MODES.MALL_PLUS_STALL].includes(mode);
  const tabs      = TAB_VISIBILITY[mode] || {};

  return (
    <div style={{ padding:'16px 16px 16px 50px', background:'var(--bg3)', borderBottom:'1px solid var(--border)' }}>
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:16 }}>

        {/* Mode selector */}
        <div>
          <div className="section-label" style={{ marginBottom:8 }}>Deployment mode</div>
          <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
            {Object.keys(DEPLOYMENT_MODES).map(m => (
              <div key={m} onClick={() => {
                setMode(m);
                const soloModes = ['STANDALONE_FULL','STANDALONE_POS_BRIDGE','TABLE_QR_ONLY'];
                if (soloModes.includes(m)) setMallId('');
              }}
                style={{ display:'flex', alignItems:'flex-start', gap:10, padding:'9px 12px', borderRadius:8, cursor:'pointer',
                  border:`2px solid ${mode===m?'var(--accent)':'var(--border)'}`,
                  background: mode===m?'#3d2e10':'var(--bg2)' }}>
                <div style={{ width:16, height:16, borderRadius:'50%', border:`2px solid ${mode===m?'var(--accent)':'var(--border)'}`, background:mode===m?'var(--accent)':'transparent', marginTop:1, flexShrink:0 }} />
                <div>
                  <div style={{ fontWeight:600, fontSize:12, color:mode===m?'var(--accent)':'var(--text)' }}>
                    {MODE_ICONS[m]} {DEPLOYMENT_MODE_LABELS[m]}
                  </div>
                  <div style={{ fontSize:11, color:'var(--text3)', marginTop:1 }}>
                    {DEPLOYMENT_MODE_DESCRIPTIONS[m]}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Preview panel */}
        <div>
          {needsMall && (
            <div style={{ marginBottom:14 }}>
              <div className="section-label" style={{ marginBottom:6 }}>Assign to mall</div>
              <select value={mallId} onChange={e => setMallId(e.target.value)}>
                <option value="">— Select mall —</option>
                {malls.map(m => <option key={m.id} value={m.id}>🏬 {m.name}</option>)}
              </select>
              {needsMall && !mallId && (
                <div style={{ fontSize:11, color:'var(--red)', marginTop:4 }}>Mall required for this mode</div>
              )}
            </div>
          )}

          <div className="section-label" style={{ marginBottom:8 }}>Tabs this vendor will see</div>
          <div style={{ display:'flex', flexWrap:'wrap', gap:6 }}>
            {Object.entries(tabs).map(([tab, visible]) => (
              <span key={tab} style={{
                padding:'3px 8px', borderRadius:6, fontSize:11, fontWeight:500,
                background:visible?'#1a3d2a':'var(--bg2)',
                color:visible?'var(--green)':'var(--text3)',
                border:`1px solid ${visible?'var(--green)':'var(--border)'}`,
                textDecoration: visible ? 'none' : 'line-through',
              }}>{tab}</span>
            ))}
          </div>
        </div>
      </div>

      <div style={{ display:'flex', gap:8 }}>
        <button className="btn btn-primary"
          disabled={loading || (needsMall && !mallId)}
          onClick={() => onSave(mode, mallId)}>
          {loading ? '⏳ Saving...' : 'Save Mode'}
        </button>
        <button className="btn btn-ghost" onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}
